import { createContext, useContext, type ComponentType, type ReactNode } from 'react';

// Lifted from apps/web registry.ts so the renderer (now in shell) and
// the app's concrete registry agree on the widget prop shape.
export type WidgetProps = Record<string, unknown> & {
  widgetId?: string;
  currentNodeId?: string | null;
  walletAddress?: string;
  onComplete?: (text: string) => void;
};

export type WidgetComponent = ComponentType<WidgetProps>;

/** name → React component. Supplied by each role-app (Phase B/C). */
export type WidgetRegistry = Record<string, WidgetComponent>;

// Default = empty registry. A renderer with no provider resolves every
// widget name to undefined and falls through to its existing unknown-widget
// path — exactly the behaviour unit tests rely on.
const WidgetRegistryContext = createContext<WidgetRegistry>({});
WidgetRegistryContext.displayName = 'WidgetRegistry';

/** Supplies the widget registry to every MarkdownRenderer below it. */
export function WidgetRegistryProvider({
  value,
  children,
}: {
  value: WidgetRegistry;
  children: ReactNode;
}) {
  return (
    <WidgetRegistryContext.Provider value={value}>
      {children}
    </WidgetRegistryContext.Provider>
  );
}

export function useWidgetRegistry(): WidgetRegistry {
  return useContext(WidgetRegistryContext);
}
